/**
  * @class meleCalendarSnapshots
  * @description
  *
  * # Purpose
  * Implements `<mele-calendar-snapshots>` custom web element for showing calendar image snapshots
  */

import msysListener from 'msysListener'
import meleElement from 'meleElement'

const PROPERTIES = _.extend({}, {
  listener_id: { type: 'string', description: 'desired listener_id for the streaming listener; if not given, automatic numeric ID will be assigned' },
  channel: { type: 'integer', description: 'video channel to be used for generating snapshots' },
  value: { type: 'string', description: 'current selected time in epoch seconds' },
  unit: { type: 'string', default: 'day', enum: ['year', 'month', 'day', 'hour', 'minute'], description: 'currently selected unit' },
  source: { type: 'string', description: 'source of streaming; empty string means our application, @ means peering, Bxxxxx: means remote machine', default: 'SERVER@msysStreamer' },
  eventValueChanged: { type: 'function', description: 'Called when user clicks on any snapshot element' },
  q: { type: 'integer', description: 'quantisation (~quality) for snapshot images from 2 (best) to 31 (worst)', default: 2, min: 2, max: 31 },
  max_width: { type: 'integer', description: 'maximum width of snapshot images in pixels', default: 160 },
  max_height: { type: 'integer', description: 'maximum height of snapshot images in pixels', default: 120 },
}, meleElement._properties)

export default class meleCalendarSnapshots extends meleElement {
  init() {
    this.style.display = 'none'
    this.innerHTML = ''
    super.init()
    setTimeout(()=>this.refresh(), 10) // wait a bit to get listener_id attribute
  } // init

  channelChanged() {
    this.refresh()
  } // channelChanged

  unitChanged() {
    this.refresh()
  } // unitChanged

  sourceChanged() {
    this.refresh()
  } // sourceChanged

  valueChanged() {
    this.refresh()
    // when refresh keeps the current snapshots (same window), scroll to the newly selected value
    this.scrollToValue()
  } // valueChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * Scrolls the snapshot closest to the currently selected value into view and highlights it.
   */
  scrollToValue() {
    const value = parseInt(this.value)
    if(!(value>0)) return
    let best = null
    let best_diff = Infinity
    for(const el of this.findAll('snapshot[dt]')) {
      const diff = Math.abs(Math.floor(parseInt(el.getAttribute('dt'))/1_000_000) - value)
      if(diff < best_diff) {
        best = el
        best_diff = diff
      }
    }
    if(!best) return
    for(const el of this.findAll('snapshot.selected')) {
      el.classList.remove('selected')
    }
    best.classList.add('selected')
    best.scrollIntoViewIfNeeded({ behavior: 'instant', block: 'center' })
  } // scrollToValue

  async refresh() {
    const channel = this.channel
    const value = parseInt(this.value)
    const level = this.unit
    if(!(channel>0 && value>0 && level)) {
      this.innerHTML = ''
      this.style.display = 'none'
      return
    }

    const dt = new Date(value*1000)
    let start
    let end
    if(level==='second') {
      dt.setSeconds(0)
      start = Math.floor(dt.valueOf()/1000)
      end = start + 59
    }else if(level==='minute') {
      dt.setMinutes(0)
      start = Math.floor(dt.valueOf()/1000/60)
      end = start + 59
    }else if(level==='hour') {
      dt.setHours(0)
      start = Math.floor(dt.valueOf()/1000/60/60)
      end = start + 23
    }else if(level==='day') {
      const days = new Date(dt.getFullYear(), dt.getMonth() + 1, 0).getDate()
      dt.setDate(1)
      start = Math.floor(dt.valueOf()/1000/60/60/24)
      end = start + days - 1
    }else {
      this.innerHTML = ''
      this.style.display = 'none'
      return
    }

    if(this.last_channel===channel && this.last_start===start) return

    const call_idx = this._refreshGen = (this._refreshGen || 0) + 1
    const aborted = () => !this.isConnected || call_idx!==this._refreshGen

    this.innerHTML = ''
    this.style.display = 'none'
    this.last_start = start
    this.last_channel = channel

    if(!this.listener) this.listener = new msysListener(this.listener_id || 0)
    if(!this.listener.listener_id) {
      // creation of listener failed as there is already one running
      this.listener = this.listener._listeners[this.listener_id]?.ref
      if(!this.listener) return
    }

    try{
      const timeout = 100
      const { params: { list } } = await this.listener.request(this.source, 'mmGetKeyframeTimes', { channel, level, start, end, timeout }, { resolve: 'mmFrameTimes', timeout: 2000 })
      if(aborted()) return
      let something_appended = false
      let scrolled = false
      for(const dt of list) {
        if(aborted()) return
        try{
          const { params: { payload } } = await this.listener.request(this.source, 'mmGetJPEG', {
            channel,
            dt,
            around: 0,
            q: this.q || 2,
            max_width: this.max_width || 160,
            max_height: this.max_height || 120,
          }, 'mmJPEG')
          if(aborted()) return
          const blob = new Blob([payload], { type: 'image/jpeg' })
          const url = URL.createObjectURL(blob)
          let img
          try{
            img = await new Promise((resolve, reject) => {
              const img = new Image()
              img.onload = () => resolve(img)
              img.onerror = () => reject(new Error('Failed to load image'))
              img.src = url
            })
          }finally{
            URL.revokeObjectURL(url)
          }
          if(aborted()) return
          const title = document.createElement('div')
          const [date_str, time_str] = _.userdatetime(Math.floor(dt/1000)).split(' ')
          title.innerHTML = `<date>${date_str}</date><time>${time_str}</time>`
          const el = document.createElement('snapshot')
          el.setAttribute('dt', dt)
          el.innerHTML = '<div class="left"></div><svg id="arrow.e" viewBox="-1 -1 32 32"><polyline points="8.5 0.5 21.5 15 8.5 29.5" fill="none" stroke="var(--fg2)" class="rounded"></polyline></svg>'
          const elLeft = el.find('.left')
          elLeft.append(title)
          elLeft.append(img)
          elLeft.onclick = this.clicked.bind(this, dt, false)
          const elSvg = el.find('svg')
          elSvg.onclick = this.clicked.bind(this, dt, true)
          if(aborted()) return
          this.appendChild(el)
          if(!something_appended) {
            this.style.display = ''
            something_appended = true
          }
          // scroll to the snapshot of the selected value as soon as it is appended
          if(!scrolled && Math.floor(dt/1_000_000)===parseInt(this.value)) {
            this.scrollToValue()
            scrolled = true
          }
        }catch(e) {
          if(aborted()) return
          this.error('failed to obtain snapshot image: '+e)
        }
      }
      // no snapshot matched the selected value exactly, scroll to the closest one
      if(!scrolled && !aborted()) this.scrollToValue()
    }catch(e) {
      if(!aborted()) this.error(e)
    }
  } // refresh

  disconnectedCallback() {
    if(this.isParkedDisconnect && this.isParkedDisconnect()) return
    if(this.listener) {
      this.listener.mmExit(null, { delete: true })
      delete this.listener
    }
  } // disconnectedCallback

  clicked(usec, seek) {
    const old = this.value
    const value = Math.floor(usec/1_000_000)
    if(seek) {
      const elModal = this.closest('mele-modal')
      if(elModal?.performSeek) return elModal.performSeek(usec)
    }
    this.value = value
    if(this.eventValueChanged) this.eventValueChanged(this, value, old, true)
  } // clickedImage
} // class meleCalendarSnapshots

meleCalendarSnapshots._properties = PROPERTIES
meleCalendarSnapshots.observedAttributes = _.keys(PROPERTIES)
window.customElements.define('mele-calendar-snapshots', meleCalendarSnapshots)
